import {
    Api, ArrayUtil, GeneralSettings, Keys, LogManager, MenuItem, Navigation, StringUtil, Styles
} from "@mcleod/core";
import { ImageName, ImageNameUtil } from "@mcleod/images";
import { Button, ClickListener, KeyEvent, Panel, PanelProps } from "@mcleod/components";
import { MainMenu } from "./MainMenu";
import { MenuLauncher } from "./MenuLauncher";
import { MenuFavorites } from "./MenuFavorites";

const log = LogManager.getLogger("common/PanelToolbox");

export class PanelToolbox extends Panel {
    private mainMenu: MainMenu;
    private panelToolboxButtons: Panel;

    private constructor(mainMenu: MainMenu, toolboxItems: MenuItem[], props?: Partial<PanelProps>) {
        super(props);
        this.mainMenu = mainMenu;
        this.panelToolboxButtons = new Panel({ id: "panelMenuToolboxImageRow", rowBreakDefault: false });
        this.panelToolboxButtons.addKeyDownListener((event: KeyEvent) => this.handleToolboxImageKey(event));
        this.add(this.panelToolboxButtons);
        this.addToolboxButtons(toolboxItems);
    }

    addToolboxButtons(toolboxItems: MenuItem[]) {
        this.addHomeButton();
        for (const toolboxItem of toolboxItems) {
            this.addToolboxButton(toolboxItem);
        }
    }

    private addHomeButton() {
        const homeButton = this.createToolboxButton({
            path: "",
            caption: "Home",
            image: ImageName.home
        }, () => {
            Navigation.navigateTo("");
            this.mainMenu.hide();
        });
        this.panelToolboxButtons.add(homeButton);
    }

    firstToolboxButton() {
        return this.panelToolboxButtons.components[0];
    }

    lastToolboxButton() {
        return ArrayUtil.getLastElement(this.panelToolboxButtons.components);
    }

    focusFirstToolboxItem() {
        this.firstToolboxButton()?.focus();
    }

    focusLastToolboxItem() {
        this.lastToolboxButton()?.focus();
    }

    private handleToolboxImageKey(event: KeyEvent) {

        if (event.isKey(Keys.ARROW_UP, {})) {
            this.mainMenu.closeButton.focus();
            event.consume();
            return;
        }

        if (event.isKey(Keys.ARROW_DOWN, {})) {
            this.mainMenu.selectFirstFavorite();
            event.consume();
            return;
        }

        if (event.isKey(Keys.ARROW_LEFT, {}) || event.isKey(Keys.ARROW_RIGHT, {})) {
            const currentIndex = this.panelToolboxButtons.components.findIndex(c => c.hasFocus());
            if (currentIndex === -1) return;

            const nextIndex = event.isKey(Keys.ARROW_RIGHT, {})
                ? currentIndex + 1
                : currentIndex - 1;

            const boundedIndex = Math.max(0, Math.min(nextIndex, this.panelToolboxButtons.components.length - 1));
            this.panelToolboxButtons.components[boundedIndex]?.focus();
        }
    }

    private addToolboxButton(toolboxItem: MenuItem){
        let menuItem = MainMenu.findMenuItemForPath(toolboxItem.path);
        if (menuItem != null) {
            if (toolboxItem.image != null && menuItem?.image == null) {
                log.debug("Using image name from settings menu item");
                menuItem = { ...menuItem, image: toolboxItem.image };
            }
            this.panelToolboxButtons.add(this.createToolboxButton(menuItem));
        } else if (toolboxItem.path.startsWith("https")) {
            this.panelToolboxButtons.add(this.createToolboxButton(toolboxItem));
        }
    }

    private createToolboxButton(item: MenuItem, clickListener?: ClickListener): Button {
        const openPageInSlideUp = !item?.path?.startsWith('https');
        const result = new Button({
            imageName: ImageNameUtil.fromString(item.image),
            tooltip: item.caption,
            rowBreak: false,
            imageHeight: 24,
            imageWidth: 24,
            padding: 4,
            margin: 0,
            color: "component.palette.menu.secondaryColor",
            borderWidth: 0
        });

        result.addFocusListener(() => {
            this.mainMenu.unselectAllMenus();
        });

        if (clickListener != null) {
            result.addClickListener(clickListener);
        } else if (item.caption === "Mpact.RatePro") {
            result.addClickListener(() => {
                Api.post("lme/dispatch/open-mpact-url").then((result) => {
                    const apiData = result.data[0];
                    if (apiData) {
                        window.open(apiData.encrypted_url, "_blank");
                    }
                });
            })
        } else if (item.path.startsWith("https")) {
            result.addClickListener(() => window.open(item.path, "", "popup"));
        } else {
            const clickListener = new MenuLauncher(this.mainMenu).openPageOnClick(item, openPageInSlideUp);
            result.addClickListener(clickListener);
        }
        return result;
    }

    public static create(mainMenu: MainMenu): PanelToolbox {
        const toolboxItemsFromSettings = GeneralSettings.get().menu_toolbox_items;
        if (ArrayUtil.isEmptyArray(toolboxItemsFromSettings) === true) {
            log.debug("No menu toolbox items are available for this user.")
            return null;
        }
        return new PanelToolbox(mainMenu, toolboxItemsFromSettings, {id: "panelMenuToolbox", fillRow: true});
    }
}
